<template>
  <div v-if="customer" class="py-6">
    <div class="flex justify-between items-center space-x-8 mb-8 border-b pb-4 dark:border-gray-800">
      <div>
        <h4 class="text-lg mr-4 leading-none font-semibold">
          {{ $t('components.customer.profile.details.title') }}
        </h4>
      </div>
    </div>
    <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
      <o-panel-link
        v-if="$features.get('loyalty')"
        tag="o-statistic"
        :path="`/customer/loyalty/${customer.idCustomer}`"
        :title="$t('general.loyaltyPoints')"
        :value="`${ customer.balances.loyaltyPoints || 0 } ${ customer.balances.loyaltyPointsInCurrencyLabel || $currency.format(0) }`"
        icon="loyaltyPoints"
        class="hover:scale-102 cursor-pointer transition-transform"
      />

      <o-panel-link
        tag="o-statistic"
        :path="`/customer/credit/${customer.idCustomer}`"
        :title="$t('general.creditBalance')"
        :value="customer.balances.creditLabel"
        icon="customerCredit"
        class="hover:scale-102 cursor-pointer transition-transform"
      />

      <o-statistic
        :title="$t('components.customer.profile.details.checkinRate')"
        :value="`${ Math.round(customer.stats.checkinRate * 100) }% (${ customer.stats.checkinCount })`"
        icon="reservationCheckIn"
        variant="success"
      />
      <o-statistic
        :title="$t('components.customer.profile.details.noShowRate')"
        :value="`${ Math.round(customer.stats.noShowRate * 100) }% (${ customer.stats.noShowCount })`"
        icon="reservationNoShow"
        variant="warning"
      />
      <o-statistic
        :title="$t('components.customer.profile.details.cancellationRate')"
        :value="`${ Math.round(customer.stats.cancellationRate * 100) }% (${ customer.stats.cancellationCount })`"
        icon="reservationCancel"
        variant="danger"
      />

      <template v-if="!customer.isArchived && $grants.all({ report: ['view'] })">
        <o-statistic
          :title="$t('general.appointments')"
          :value="customer.stats.reservationCount"
          icon="reservation"
        />
        <o-statistic
          :title="$t('general.revenue')"
          :value="$currency.format(customer.stats.reservationRevenue)"
          icon="currency"
        />
        <o-statistic
          v-if="customer.stats.reservationRevenue && customer.stats.reservationCount"
          :title="$t('customer.averageValue')"
          :value="$currency.format(customer.stats.reservationRevenue / customer.stats.reservationCount)"
          icon="chart"
        />
        <o-statistic
          v-if="customer.stats.reservationDuration"
          :title="$t('components.customer.profile.details.totalAppointmentDuration')"
          :value="reservationDurationLabel"
          icon="reservation"
        />
        <o-statistic
          v-if="customer.stats.reservationDuration && customer.stats.reservationCount"
          :title="$t('components.customer.profile.details.averageAppointmentDuration')"
          :value="averageReservationDuration"
          icon="reservation"
        />
      </template>
    </div>

    <div class="flex flex-col divide-y overflow-hidden p-6 items-center h-full bg-white shadow-lg rounded-2xl mb-4 dark:bg-gray-800/20 dark:border dark:border-gray-800">
      <dl class="w-full">
        <o-description-details :term="$t('general.name')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          {{ customer.displayName }}
        </o-description-details>

        <o-description-details :term="$t('general.company')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          {{ customer.company }}
        </o-description-details>

        <o-description-details :term="$t('components.customer.profile.details.age')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          {{ customer.age }}
        </o-description-details>

        <o-description-details :term="$t('general.dateOfBirth')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          {{ customer.dateOfBirthLabel }}
        </o-description-details>

        <o-description-details :term="$t('general.address')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          <a :href="`https://www.google.com/maps/search/?api=1&query=${customer.addressLabel}`" target="_blank">
            {{ customer.addressLabel }}
          </a>
        </o-description-details>

        <o-description-details :term="$t('general.phone')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          <o-dropdown>
            <p class="text-sm text-primary-600 cursor-pointer">
              {{ customer.phone }}
            </p>

            <template #content>
              <o-dropdown-item :description="$t('customer.callInfo')" :href="'tel:'+customer.phone">
                {{ $t('customer.call') }}
              </o-dropdown-item>

              <o-panel-link
                v-if="$grants.all({ marketing: ['send'] })"
                tag="o-dropdown-item"
                :description="$t('customer.sendSMSInfo')"
                path="/message/send/sms"
                :props="{ customers: [ customer ] }"
                class="text-primary-600"
              >
                {{ $t('general.sendSMS') }}
              </o-panel-link>
            </template>
          </o-dropdown>
        </o-description-details>

        <o-description-details :term="$t('general.homePhone')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          <a :href="'tel:'+customer.homePhone" class="text-primary-600">
            {{ customer.homePhone }}
          </a>
        </o-description-details>

        <o-description-details :term="$t('general.email')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          <o-panel-link path="/message/send/email" :props="{ customers: [ customer ] }" class="text-primary-600">
            {{ customer.email }}
          </o-panel-link>
        </o-description-details>

        <o-description-details :term="$t('general.gender')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          {{ customer.gender ? $t(CustomerGenderLabels[customer.gender]) : '' }}
        </o-description-details>

        <o-description-details :term="$t('components.customer.profile.details.customerSince')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
          {{ customer.createdLabel }}
        </o-description-details>

        <div v-if="customFields.length" class="border-t mt-3 pt-3 dark:border-gray-800">
          <template v-for="customField in customFields" :key="customField.idCustomField">
            <o-description-details :term="customField.name" term-col="sm:col-span-2" definition-col="sm:col-span-4">
              {{ customField.valueLabel ? customField.valueLabel : '-' }}
            </o-description-details>
          </template>
        </div>
      </dl>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CustomerGenderLabels } from '@/_api/models/customer'
import {
  ODescriptionDetails,
  ODropdownItem,
  OStatistic,
} from '@/components/elements'

interface Props {
  customer: Record<string, any>
}

const props = defineProps<Props>()

const customFields = computed(() => props.customer.customFields)

const reservationDurationLabel = computed(() => {
  const duration = props.customer.stats.reservationDuration
  const hours = Math.floor(duration / 60)
  const minutes = Math.round(duration % 60)

  if (hours === 0) {
    return `${minutes}m`
  } else {
    return `${hours}h ${minutes}m`
  }
})

const averageReservationDuration = computed(() => {
  const avg = props.customer.stats.reservationDuration / props.customer.stats.reservationCount

  const hours = Math.floor(avg / 60)
  const minutes = Math.round(avg % 60)

  if (hours === 0) {
    return `${minutes}m`
  } else {
    return `${hours}h ${minutes}m`
  }
})
</script>
